/**
 * Ein Katalog der Vergütungsmodelle für alle Bereiche (Kooperationen, Einstellungen, Stammdaten, Creator-Portal):
 * kurzer Name für Listen und Auswahl, Titel für Überschriften, Erklärung für das Formular. Vorher hießen dieselben
 * Modelle je Seite anders („Festbudget“, „Einmalig“, „Nur Provision“, „Reine Provision“).
 * Die Vertragssprache (DEAL_MODEL_LABEL in contract-variables.ts) bleibt bewusst ausführlicher.
 *
 * SaaS-Tauglichkeit (E-04, E-05, 0105): „Provision“ heißt nur noch der Anteil der Agentur. Vergütet die Marke den Creator
 * mit einem Anteil an ihrem Umsatz (Affiliate), heißt das Modell „Umsatzbeteiligung“ bzw. „Fest + Umsatzbeteiligung“.
 * „Event“ meint eine Einladung ohne Honorar und ohne Rechnung und heißt deshalb „Einladung / Event ohne Honorar“; ein
 * bezahlter Auftritt ist „Einmalig“. Zu jedem Modell gibt es einen Hilfetext in beiden Sprachen (dealModelHint).
 */

export const DEAL_MODEL_KEYS = [
  "fixed", "monthly", "installments", "commission_only", "fixed_plus_commission", "custom", "barter", "event", "gifting",
] as const;
export type DealModelKey = (typeof DEAL_MODEL_KEYS)[number];

export interface DealModelName {
  /** Listen, Filter, Auswahl */
  de: string;
  en: string;
  /** Überschrift einer Kooperation („Monatliche Kooperation“) */
  titleDe: string;
  titleEn: string;
  /** Ein Satz Erklärung für Formular und Auswahl (E-04: jeder Hilfetext auch auf Englisch) */
  hintDe: string;
  hintEn: string;
}

export const DEAL_MODEL_NAME: Readonly<Record<DealModelKey, DealModelName>> = {
  fixed: {
    de: "Einmalig", en: "One-off", titleDe: "Einmalige Kooperation", titleEn: "One-off collaboration",
    hintDe: "Einmalig: ein Honorar für einen Abrechnungsmonat, auch für einen bezahlten Auftritt oder ein bezahltes Event.",
    hintEn: "One-off: one fee for one billing month, also for a paid appearance or a paid event.",
  },
  monthly: {
    de: "Monatlich", en: "Monthly", titleDe: "Monatliche Kooperation", titleEn: "Monthly collaboration",
    hintDe: "Monatlich: jeder Monat hat einen eigenen Betrag und eine eigene Rechnung.",
    hintEn: "Monthly: each month has its own amount and its own invoice.",
  },
  installments: {
    de: "Raten", en: "Instalments", titleDe: "Kooperation in Raten", titleEn: "Collaboration in instalments",
    hintDe: "Raten: ein Gesamthonorar für eine Leistung, verteilt auf mehrere Monate. Die Raten ergeben zusammen das Honorar; jede Rate hat einen eigenen Stand und eine eigene Rechnung.",
    hintEn: "Instalments: one total fee for one service, spread over several months. The instalments add up to the fee; each instalment has its own status and its own invoice.",
  },
  commission_only: {
    de: "Umsatzbeteiligung", en: "Revenue share", titleDe: "Kooperation mit Umsatzbeteiligung", titleEn: "Revenue-share collaboration",
    hintDe: "Umsatzbeteiligung: kein festes Honorar; die Marke beteiligt den Creator an ihrem Umsatz (z. B. Affiliate). Je Monat steht der erwartete Anteil der Agentur als Vorschau, die Rechnung ersetzt ihn später.",
    hintEn: "Revenue share: no fixed fee; the brand pays the creator a share of its sales (e.g. affiliate). Each month shows the agency's expected share as a preview; the invoice replaces it later.",
  },
  fixed_plus_commission: {
    de: "Fest + Umsatzbeteiligung", en: "Fixed + revenue share", titleDe: "Festbetrag und Umsatzbeteiligung", titleEn: "Fixed fee plus revenue share",
    hintDe: "Fest + Umsatzbeteiligung: je Monat ein festes Honorar und dazu eine erwartete Umsatzbeteiligung der Marke.",
    hintEn: "Fixed + revenue share: each month a fixed fee plus an expected revenue share from the brand.",
  },
  custom: {
    de: "Individuell", en: "Custom", titleDe: "Individuelle Kooperation", titleEn: "Custom collaboration",
    hintDe: "Individuell: Beträge je Monat; es gilt der Provisionssatz der Kooperation. Für neue Kooperationen „Monatlich“ wählen.",
    hintEn: "Custom: amounts per month; the deal's commission rate applies. For new deals choose “Monthly”.",
  },
  barter: {
    de: "Barter", en: "Barter", titleDe: "Barter-Kooperation", titleEn: "Barter collaboration",
    hintDe: "Barter: Produkte als Gegenleistung, auf Wunsch mit Honorar. Der Warenwert wird nie geteilt und nie abgerechnet; ein Honorar wird geteilt und abgerechnet.",
    hintEn: "Barter: products in return, optionally with a fee. The value of goods is never split and never invoiced; a fee is split and invoiced.",
  },
  event: {
    de: "Einladung / Event ohne Honorar", en: "Invitation / event without fee", titleDe: "Einladung / Event ohne Honorar", titleEn: "Invitation / event without fee",
    hintDe: "Einladung / Event ohne Honorar: keine Rechnung, zählt nicht als Umsatz. Ein Warenwert (z. B. Reise, Produkte) lässt sich erfassen. Für einen bezahlten Auftritt „Einmalig“ wählen.",
    hintEn: "Invitation / event without fee: no invoice, does not count as revenue. A value of goods (e.g. travel, products) can be recorded. For a paid appearance choose “One-off”.",
  },
  gifting: {
    de: "Gifting", en: "Gifting", titleDe: "Gifting", titleEn: "Gifting",
    hintDe: "Gifting: Produkte ohne Honorar und ohne Rechnung, zählt nicht als Umsatz. Der Warenwert ist Grundlage einer Pauschalversteuerung.",
    hintEn: "Gifting: products without fee and without invoice, does not count as revenue. The value of goods is the basis for flat-rate taxation.",
  },
};

/** Name eines Modells; unbekannte Schlüssel bleiben sichtbar statt leer. */
export function dealModelName(key: string, lang: "de" | "en" = "de"): string {
  const m = (DEAL_MODEL_NAME as Record<string, DealModelName>)[key];
  return m ? m[lang] : key;
}
export function dealModelTitle(key: string, lang: "de" | "en" = "de"): string {
  const m = (DEAL_MODEL_NAME as Record<string, DealModelName>)[key];
  return m ? (lang === "en" ? m.titleEn : m.titleDe) : key;
}
/** Hilfetext eines Modells (ein Satz, DE und EN); unbekannte Schlüssel ergeben einen leeren Text. */
export function dealModelHint(key: string, lang: "de" | "en" = "de"): string {
  const m = (DEAL_MODEL_NAME as Record<string, DealModelName>)[key];
  return m ? (lang === "en" ? m.hintEn : m.hintDe) : "";
}
